import { Injectable } from '@angular/core';
import { concat, Observable, of } from 'rxjs';
import { map, switchMap, tap, toArray } from 'rxjs/operators';

import { ClientProvider } from '../providers/client.provider';
import { ContractProvider } from '../providers/contract.provider';
import { ContractService } from '../services/contract.service';
import { Transaction } from '../transaction/transaction';
import { UseCase } from './common/use.case';
import { ValidationStatus } from '../data/enum/validation-status.enum';

@Injectable()
export class ValidateContractUseCase implements UseCase<Observable<Transaction<void>>> {
    constructor(
        private contractService: ContractService,
        private contractProvider: ContractProvider,
        private clientProvider: ClientProvider
    ) {}

    bind(): Observable<Transaction<void>> {
        return this.contractProvider.current().pipe(
            switchMap((transaction) =>
                transaction.map(
                    (contract) =>
                        this.contractService.getParticipants(true).pipe(
                            switchMap((transactionParticipants) =>
                                transactionParticipants.map(
                                    (participants) => {
                                        let errorMsg: string;
                                        const observables: Observable<Transaction<void>>[] = [];

                                        participants.map((client: any) => {
                                            if (client.validation.status === ValidationStatus.REVISE) {
                                                observables.push(
                                                    this.clientProvider
                                                        .revise(contract, client, client.validation.reason)
                                                        .pipe(
                                                            tap((transactionRevise) =>
                                                                !transactionRevise.isSuccess && !errorMsg
                                                                    ? (errorMsg = transactionRevise.error)
                                                                    : null
                                                            )
                                                        )
                                                );
                                            } else if (client.validation.status === ValidationStatus.REJECT) {
                                                observables.push(
                                                    this.clientProvider
                                                        .reject(contract, client, client.validation.reason)
                                                        .pipe(
                                                            tap((transactionReject) =>
                                                                !transactionReject.isSuccess && !errorMsg
                                                                    ? (errorMsg = transactionReject.error)
                                                                    : null
                                                            )
                                                        )
                                                );
                                            } else if (client.validation.status === ValidationStatus.VALID) {
                                                observables.push(
                                                    this.clientProvider
                                                        .validate(client)
                                                        .pipe(
                                                            tap((transactionValidate) =>
                                                                !transactionValidate.isSuccess && !errorMsg
                                                                    ? (errorMsg = transactionValidate.error)
                                                                    : null
                                                            )
                                                        )
                                                );
                                            }
                                        });

                                        if (contract.validation.status === ValidationStatus.REVISE) {
                                            observables.push(
                                                this.contractProvider
                                                    .revise(contract, contract.validation.reason)
                                                    .pipe(
                                                        tap((transactionRevise) =>
                                                            !transactionRevise.isSuccess && !errorMsg
                                                                ? (errorMsg = transactionRevise.error)
                                                                : null
                                                        )
                                                    )
                                            );
                                        } else if (contract.validation.status === ValidationStatus.VALID) {
                                            observables.push(
                                                this.contractProvider
                                                    .validate(contract)
                                                    .pipe(
                                                        tap((transactionValidate) =>
                                                            !transactionValidate.isSuccess && !errorMsg
                                                                ? (errorMsg = transactionValidate.error)
                                                                : null
                                                        )
                                                    )
                                            );
                                        } else if (contract.validation.status === ValidationStatus.REJECT) {
                                            observables.push(
                                                this.contractProvider
                                                    .reject(contract, '')
                                                    .pipe(
                                                        tap((transactionReject) =>
                                                            !transactionReject.isSuccess && !errorMsg
                                                                ? (errorMsg = transactionReject.error)
                                                                : null
                                                        )
                                                    )
                                            );
                                        }

                                        observables.push(
                                            this.contractProvider
                                                .validateNotification(contract)
                                                .pipe(
                                                    tap((transactionNotification) =>
                                                        !transactionNotification.isSuccess && !errorMsg
                                                            ? (errorMsg = transactionNotification.error)
                                                            : null
                                                    )
                                                )
                                        );

                                        return concat(...observables).pipe(
                                            toArray(),
                                            map(() => {
                                                if (errorMsg) {
                                                    return Transaction.error<void>(errorMsg);
                                                }

                                                return Transaction.success<void>();
                                            })
                                        );
                                    },
                                    (throwable) => of(Transaction.error(throwable.message))
                                )
                            )
                        ),
                    (throwable) => of(Transaction.error(throwable.message))
                )
            )
        );
    }
}
